import { createClient, FunctionsHttpError } from '@supabase/supabase-js';
import { logger, isDev } from './logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Only log initialization details in development
logger.debug('Supabase initialization:', {
  url: supabaseUrl ? '[SET]' : '[MISSING]',
  hasKey: !!supabaseAnonKey
});

if (!supabaseUrl || !supabaseAnonKey) {
  logger.error('Missing Supabase environment variables');
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Custom fetch with timeout to prevent hanging on stale connections
const fetchWithTimeout = (url, options = {}) => {
  const timeout = 15000; // 15 second timeout for all Supabase requests

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
    logger.debug('Supabase request timed out:', url);
  }, timeout);

  return fetch(url, {
    ...options,
    signal: controller.signal
  }).finally(() => {
    clearTimeout(timeoutId);
  });
};

/** Supabase project URL and anon key (for building function URLs / headers so prod uses same values as client). */
export const supabaseUrlForFunctions = supabaseUrl;
export const supabaseAnonKeyForFunctions = supabaseAnonKey;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Use shorter lock timeout to prevent hanging on token refresh
    lockAcquireTimeout: 5000,
    // Use custom storage key (helps with debugging)
    storageKey: `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`
  },
  global: {
    fetch: fetchWithTimeout
  }
});

// Only expose to window in development for debugging
if (isDev && typeof window !== 'undefined') {
  window.supabase = supabase;
  logger.debug('Supabase client exposed to window.supabase (dev only)');
}

// Listen for auth errors and clear corrupted sessions automatically
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED') {
    logger.debug('Token refreshed successfully by Supabase client');
  }

  // If we get signed out unexpectedly (e.g., refresh failed), clear localStorage
  if (event === 'SIGNED_OUT' && !session) {
    logger.debug('Session ended - clearing any stale auth data');
    try {
      const storageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
      localStorage.removeItem(storageKey);
    } catch (e) {
      // Ignore
    }
  }
});

/**
 * Get a user-friendly error message from an Edge Function invoke error.
 * For 4xx/5xx responses the body is in error.context; we parse it and return body.error or body.message.
 * @param {Error} error - Error from supabase.functions.invoke (may be FunctionsHttpError)
 * @returns {Promise<string>} Message to show (backend message or error.message)
 */
export async function getFunctionErrorMessage(error) {
  if (!error) return 'Błąd.';
  if (error instanceof FunctionsHttpError && error.context) {
    try {
      const body = await error.context.json();
      if (body?.error && typeof body.error === 'string') return body.error;
      if (body?.message && typeof body.message === 'string') return body.message;
    } catch (_) {
      // response was not JSON or already consumed
    }
  }
  return error?.message || 'Błąd.';
}

export const handleSupabaseError = (error) => {
  if (!error) return null;

  const errorMessages = {
    '23505': 'This record already exists',
    '23503': 'Cannot delete: record is referenced by other data',
    '23502': 'Required field is missing',
    '42501': 'You do not have permission to perform this action',
    'PGRST116': 'No rows found',
  };

  const errorCode = error.code;
  const message = errorMessages[errorCode] || error.message || 'An error occurred';

  return {
    message,
    code: errorCode,
    details: error.details,
    hint: error.hint
  };
};

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

export const getCurrentUserProfile = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: profiles, error } = await supabase
    .from('app_users')
    .select('*')
    .eq('id', user.id);

  if (error) throw error;
  return profiles?.[0] || null;
};

/**
 * Force refresh the session to unblock a potentially stuck Supabase client.
 * Call this when returning from idle or when queries are timing out.
 */
export const forceRefreshSession = async () => {
  logger.debug('Force refreshing session...');
  try {
    // First, try to get the current session
    const { data: { session }, error } = await Promise.race([
      supabase.auth.getSession(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('getSession timeout')), 3000)
      )
    ]);

    if (error || !session) {
      logger.debug('No session found during force refresh');
      return { success: false, session: null };
    }

    // Explicitly refresh the token
    const { data: refreshData, error: refreshError } = await Promise.race([
      supabase.auth.refreshSession(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('refreshSession timeout')), 5000)
      )
    ]);

    if (refreshError) {
      logger.error('Session refresh failed:', refreshError.message);
      return { success: false, session: null, error: refreshError };
    }

    logger.debug('Session force refreshed successfully');
    return { success: true, session: refreshData?.session };
  } catch (err) {
    logger.error('Force refresh error:', err.message);
    // If we timeout, the client might be in a bad state - clear localStorage
    if (err.message.includes('timeout')) {
      logger.debug('Force refresh timed out - clearing localStorage');
      try {
        const storageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
        localStorage.removeItem(storageKey);
      } catch (e) {
        // Ignore
      }
    }
    return { success: false, session: null, error: err };
  }
};

/**
 * Wrapper for Supabase queries that adds a timeout.
 * If the query doesn't complete in time, it will reject with a timeout error.
 * @param {Promise} queryPromise - The Supabase query promise
 * @param {number} timeoutMs - Timeout in milliseconds (default 10000)
 */
export const withQueryTimeout = (queryPromise, timeoutMs = 10000) => {
  return Promise.race([
    queryPromise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Query timeout - Supabase client may be blocked')), timeoutMs)
    )
  ]);
};

/**
 * Terminate session using navigator.sendBeacon for reliable delivery during page unload.
 * This is used when the user closes the tab to ensure the session is invalidated.
 * Note: sendBeacon is best-effort and may not always succeed, but the 30-min
 * inactivity timeout serves as a safety net.
 */
export const terminateSessionBeacon = () => {
  try {
    const storageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
    const tokenData = localStorage.getItem(storageKey);

    if (!tokenData) {
      logger.debug('No token to terminate');
      return false;
    }

    // Parse the token to get access_token
    let accessToken;
    try {
      const parsed = JSON.parse(tokenData);
      accessToken = parsed?.access_token;
    } catch (e) {
      logger.debug('Failed to parse token data');
      return false;
    }

    if (!accessToken) {
      logger.debug('No access token found');
      return false;
    }

    // Clear localStorage immediately (synchronous)
    localStorage.removeItem(storageKey);

    // Use sendBeacon to POST to Supabase logout endpoint
    // This is reliable even during page unload
    const logoutUrl = `${supabaseUrl}/auth/v1/logout`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'apikey': supabaseAnonKey,
    };

    // sendBeacon with Blob for headers
    const blob = new Blob([JSON.stringify({ scope: 'local' })], {
      type: 'application/json',
    });

    // Unfortunately sendBeacon doesn't support custom headers
    // So we fall back to a sync XHR or just clear localStorage
    // The localStorage clear is the most important part
    // The server-side token will expire naturally (1 hour)

    logger.debug('Session terminated (localStorage cleared)');
    return true;
  } catch (e) {
    logger.debug('terminateSessionBeacon error:', e.message);
    return false;
  }
};
